import { useEffect, useMemo, useState } from "react";
import { Loader2, Search, UserMinus, UserPlus, Users, X } from "lucide-react";
import { useAddTeamMember, type TeamRead } from "@/hooks/use-teams";
import { useUserSearch, type UserSummary } from "@/hooks/use-users";

// Mejora D (changelog cliente): diálogo de miembros de un equipo.
// Lista los miembros con opción de quitar y un picker de búsqueda por
// email/nombre (≥2 caracteres, debounced 300ms) para añadir nuevos.

interface TeamMembersDialogProps {
  isOpen: boolean;
  onClose: () => void;
  team: TeamRead | null;
  isLoading: boolean;
  onRemoveMember: (userId: string) => void;
  removingUserId?: string;
}

export function TeamMembersDialog({
  isOpen,
  onClose,
  team,
  isLoading,
  onRemoveMember,
  removingUserId,
}: TeamMembersDialogProps) {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const addMutation = useAddTeamMember();

  // Debounce 300ms para no bombardear /users/search con cada tecla.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(searchInput), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Reset al cerrar el diálogo.
  useEffect(() => {
    if (!isOpen) {
      setSearchInput("");
      setDebouncedQuery("");
    }
  }, [isOpen]);

  const { data: searchResults, isFetching: searchLoading } =
    useUserSearch(debouncedQuery);

  // Excluimos a quienes ya son miembros para no ofrecerlos en el picker.
  const memberIds = useMemo(
    () => new Set((team?.members ?? []).map((m) => m.user_id)),
    [team]
  );
  const availableResults = useMemo(
    () => (searchResults ?? []).filter((u) => !memberIds.has(u.id)),
    [searchResults, memberIds]
  );

  if (!isOpen) return null;

  const handleAdd = (user: UserSummary) => {
    if (!team) return;
    addMutation.mutate(
      { teamId: team.id, userId: user.id },
      {
        onSuccess: () => {
          setSearchInput("");
          setDebouncedQuery("");
        },
      }
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Miembros del equipo"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Miembros de {team?.name ?? "—"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
            </div>
          )}

          {team && (team.members?.length ?? 0) === 0 && (
            <p className="text-sm text-gray-500">
              Este equipo no tiene miembros aún. Añade el primero abajo.
            </p>
          )}

          {team && (team.members?.length ?? 0) > 0 && (
            <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md">
              {(team.members ?? []).map((m) => (
                <li
                  key={m.user_id}
                  className="px-3 py-2 flex items-center justify-between"
                >
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {m.name || m.email}
                    </div>
                    <div className="text-xs text-gray-500">
                      {m.email}
                      <span className="text-gray-300"> · </span>
                      <code className="text-[10px]">{m.user_id}</code>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemoveMember(m.user_id)}
                    disabled={removingUserId === m.user_id}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                  >
                    {removingUserId === m.user_id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <UserMinus className="w-3 h-3" />
                    )}
                    Quitar
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="pt-2 border-t border-gray-100">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Añadir miembro
            </label>
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Buscar por email o nombre (≥2 caracteres)…"
                disabled={addMutation.isPending}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {debouncedQuery.length >= 2 && (
              <div className="mt-2 border border-gray-200 rounded-md max-h-48 overflow-y-auto">
                {searchLoading && (
                  <div className="flex items-center gap-2 px-3 py-2 text-xs text-gray-500">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Buscando…
                  </div>
                )}
                {!searchLoading && availableResults.length === 0 && (
                  <div className="px-3 py-2 text-xs text-gray-500">
                    Sin resultados.{" "}
                    {(searchResults?.length ?? 0) > 0
                      ? "Todos los coincidentes ya son miembros."
                      : "El usuario debe haber accedido a LicitaAI al menos una vez."}
                  </div>
                )}
                {!searchLoading && availableResults.length > 0 && (
                  <ul className="divide-y divide-gray-100">
                    {availableResults.map((u) => (
                      <li
                        key={u.id}
                        className="px-3 py-2 flex items-center justify-between hover:bg-gray-50"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {u.name || u.email}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {u.email}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAdd(u)}
                          disabled={
                            addMutation.isPending &&
                            addMutation.variables?.userId === u.id
                          }
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50"
                        >
                          {addMutation.isPending &&
                          addMutation.variables?.userId === u.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <UserPlus className="w-3 h-3" />
                          )}
                          Añadir
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
